import { ItemBar } from "components/ItemBar";
import { ToastContext } from "context/ToastContext";
import { prepareCodesForSubmit, CodeRangeError } from "helpers/codeRanges";
import { Button } from "primereact/button";
import { useContext } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useApplyPackageRulesetPropagationMutation,
  useLazyGetPackageRulesetPropagationPreviewQuery,
  useUpdatePackageRulesetGroupMutation,
  useUpdatePackageRulesetMutation,
} from "store/queries/packageRuleset";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";

import {
  formApplicability,
  mapPackagesFormToRequestBody,
} from "../helpers/mapFormToRequestBody";
import {
  scopeUpdateFields,
  shouldOfferPropagation,
} from "../helpers/ruleApplicability";
import { useSubmitRuleGroup } from "../hooks/useSubmitRuleGroup";
import { useRuleScopeComplete } from "./RuleApplicabilityField";
import { usePropagateAfterEdit } from "./usePropagateAfterEdit";

interface ISubmitEditPackagesProps {
  id: string;
  /**
   * CLE-2842 step 2 — the applicability the rule was loaded with. A save that converts the rule
   * (Payer-Provider ↔ Fallback) sends the new `applicability`; any other save sends none.
   */
  savedApplicability?: RulesetApplicability;
}

export function SubmitEditPackages({
  id,
  savedApplicability,
}: ISubmitEditPackagesProps) {
  const {
    getValues,
    formState: { isValid },
  } = useFormContext();
  // CLE-2842 step 2 — a Payer-Provider rule needs its provider and payer, whatever the
  // scope fields' mount timing did to react-hook-form's own validity.
  const scopeComplete = useRuleScopeComplete();
  const navigate = useNavigate();
  const [updatePackageRuleset] = useUpdatePackageRulesetMutation();
  const [updatePackageRulesetGroup] = useUpdatePackageRulesetGroupMutation();
  // CLE-2842 — non-null when this rule is edited for every payer in its bucket at once.
  const submitGroup = useSubmitRuleGroup(updatePackageRulesetGroup);

  const { current: toastElement } = useContext(ToastContext);

  const goBackToList = () => navigate("/payer-rules");

  const { openForId, modal } = usePropagateAfterEdit({
    useLazyPreview: useLazyGetPackageRulesetPropagationPreviewQuery as any,
    useApplyPropagation: useApplyPackageRulesetPropagationMutation as any,
    onDone: goBackToList,
  });

  const updatePayerRule = async () => {
    const values = getValues();
    let triggerCptCodes: string[];
    let bundledCptCodes: string[];
    try {
      triggerCptCodes = prepareCodesForSubmit(values.triggerCptCodes ?? []);
      bundledCptCodes = prepareCodesForSubmit(values.bundledCptCodes ?? []);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Invalid code range",
        detail:
          err instanceof CodeRangeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    const requestBody = mapPackagesFormToRequestBody({
      ...values,
      triggerCptCodes,
      bundledCptCodes,
    });
    const applicability = formApplicability(values);
    if (submitGroup) {
      await submitGroup(requestBody);
      return;
    }
    try {
      updatePackageRuleset({
        ...requestBody,
        ...scopeUpdateFields(applicability, savedApplicability),
        id,
      })
        .unwrap()
        .then(() => {
          toastElement?.show({
            severity: "success",
            summary: "Package Rule updated",
            detail: "Package Rule updated successfully",
          });
          // Only a rule that was already Typical or Fallback has copies; a conversion returns
          // to the list.
          if (shouldOfferPropagation(applicability, savedApplicability)) {
            openForId(id);
          } else {
            goBackToList();
          }
        })
        .catch((error) => {
          if (error.status === 409) {
            toastElement?.show({
              severity: "error",
              summary: "Error",
              detail:
                "A package ruleset with this provider and payer already exists",
            });
          } else {
            toastElement?.show({
              severity: "error",
              summary: "Error",
              detail:
                "An error has occurred attempting to update the package ruleset",
            });
          }
        });
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <ItemBar>
      <div
        data-testid="submit__Hospital"
        className="flex flex-1 justify-content-end"
      >
        <Button
          label="Update Package Rule"
          onClick={updatePayerRule}
          disabled={!isValid || !scopeComplete}
        />
      </div>
      {modal}
    </ItemBar>
  );
}
